import React from 'react';
import { Button, Dropdown, Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ExportOutlined, MoreOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { RouteOrderDto, TransportRouteDetailDto } from '../../../dto/transport';
import { formatCargoQuantityPair } from '../../../utils/cargoQuantity';
import { formatLocalizedNumber, formatLocalizedQuantity } from '../../../utils/numberFormatting';
import { formatUnit } from '../../../utils/units';
import { TransportAllocatedCost } from '../TransportAllocatedCost';
import { formatLineDimensions } from '../../../utils/productDimensions';
import './TransportLinkedOrdersTable.css';

const { Text } = Typography;

type CargoItem = TransportRouteDetailDto['cargoItems'][number];

interface Row {
  key: string;
  routeOrder: RouteOrderDto;
  item: CargoItem | null;
  /** Rows of this order; > 0 only on the order's first row (antd rowSpan). */
  span: number;
}

interface Props {
  transport: TransportRouteDetailDto;
  showCosts: boolean;
  onOpenOrder: (orderId: string) => void;
  onUnlink?: (orderId: string) => void;
}

/**
 * „Powiązane zamówienia” on the truck page: one row per cargo line, the order
 * (number, customer, unloading point), its volume and freight share merged
 * over its lines. Presentation only — cargo and delivery facts are
 * backend-owned. The truck status is in the page header, not per row.
 * Fits its card without horizontal scroll down to 1024 px (owner review
 * 2026-10-22; DESIGN.md §6 „Table wider than its tile”): fixed layout, the
 * columns take shares of the table and the goods column takes the rest;
 * related figures stack in one cell (delivered with the shortfall under it,
 * volume over weight, freight over its share). The customer and the unloading
 * address are one line with „…” and the full text in `title`/tooltip.
 */
export const TransportLinkedOrdersTable: React.FC<Props> = ({ transport, showCosts, onOpenOrder, onUnlink }) => {
  const { t, i18n } = useTranslation(['transport', 'common']);

  const rows: Row[] = transport.routeOrders.flatMap((routeOrder): Row[] => {
    const items = transport.cargoItems.filter((item) => item.orderId === routeOrder.orderId);
    if (items.length === 0) return [{ key: routeOrder.orderId, routeOrder, item: null, span: 1 }];
    return items.map((item, index) => ({
      key: item.id || `${routeOrder.orderId}-${index}`,
      routeOrder,
      item,
      span: index === 0 ? items.length : 0,
    }));
  });

  const hasDelivered = transport.cargoItems.some((item) => item.deliveredQuantity != null);
  const hasWeight = transport.routeOrders.some((row) => Number(row.weight || 0) > 0);
  const hasVolume = transport.routeOrders.some((row) => Number(row.volume || 0) > 0);
  // Freight per order exists only where the truck charges its customer orders.
  const hasFreight = showCosts && transport.routeOrders.some((row) => Number(row.allocatedTransportCost || 0) > 0);
  const merged = (row: Row) => ({ rowSpan: row.span });
  const quantity = (value: number, unit: string) => `${formatLocalizedQuantity(value, i18n.language)} ${formatUnit(unit, i18n.language)}`;

  const columns: ColumnsType<Row> = [
    {
      key: 'actions',
      width: 44,
      align: 'center',
      onCell: merged,
      render: (_, row) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              { key: 'open', icon: <ExportOutlined />, label: t('transport:linkedOrdersTable.openOrder') },
              ...(onUnlink ? [{ key: 'unlink', danger: true, label: t('transport:orderTransports.unlink') }] : []),
            ],
            onClick: ({ key }) => (key === 'unlink' ? onUnlink?.(row.routeOrder.orderId) : onOpenOrder(row.routeOrder.orderId)),
          }}
        >
          <Button size="small" icon={<MoreOutlined />} title={t('common:actions')} data-testid={`transport-linked-order-actions-${row.routeOrder.orderId}`} />
        </Dropdown>
      ),
    },
    {
      key: 'order',
      title: t('transport:costSplit.columns.order'),
      width: '18%',
      onCell: merged,
      render: (_, { routeOrder }) => (
        <div className="transport-linked-orders__identity">
          <button type="button" className="transport-linked-orders__order-link" onClick={() => onOpenOrder(routeOrder.orderId)}>
            {routeOrder.order.displayOrderNumber || routeOrder.order.orderNumber}
          </button>
          <span className="transport-linked-orders__customer" title={routeOrder.order.customerName || undefined}>{routeOrder.order.customerName || '—'}</span>
          {routeOrder.deliveryAddress ? (
            <Tooltip title={routeOrder.deliveryAddress}>
              <span className="transport-linked-orders__meta">
                {t('transport:orderRoutePoints.deliveryAddress')}: {routeOrder.deliveryAddress}
              </span>
            </Tooltip>
          ) : null}
        </div>
      ),
    },
    {
      key: 'product',
      title: t('transport:supplierOrders.freight.goods'),
      render: (_, { item }) => {
        if (!item) return <Text type="secondary">{t('transport:orderTransports.noCargoForOrder')}</Text>;
        const dimensions = formatLineDimensions(item);
        return (
          <>
            <span className="transport-linked-orders__product" title={item.productName || undefined}>{item.productName}</span>
            {dimensions ? (
              <span className="transport-linked-orders__dims" title={dimensions} data-testid={`transport-linked-order-dims-${item.id}`}>{dimensions}</span>
            ) : null}
          </>
        );
      },
    },
    {
      key: 'plan',
      title: t('transport:deliveryConfirmation.planLabel'),
      align: 'right',
      width: '13%',
      className: 'transport-linked-orders__plan-col',
      // Physical and commercial amounts one under the other, each whole (DESIGN.md §6
      // „Count cut off”; staging v3.27.0 cut „32 szt. / 1…”).
      render: (_, { item }) => {
        if (!item) return null;
        const hasCommercial = Boolean(item.commercialUnit) && Number(item.commercialQuantity) > 0;
        return (
          <span className="transport-linked-orders__plan" data-testid={`transport-linked-order-plan-${item.id}`}>
            <span>{formatCargoQuantityPair({ quantity: item.quantity, unit: item.unit }, i18n.language)}</span>
            {hasCommercial ? (
              <span className="transport-linked-orders__plan-commercial">
                {formatCargoQuantityPair({ quantity: item.commercialQuantity, unit: item.commercialUnit }, i18n.language)}
              </span>
            ) : null}
          </span>
        );
      },
    },
    ...(hasDelivered ? [{
      key: 'delivered',
      title: t('transport:delivered'),
      align: 'right' as const,
      width: '17%',
      render: (_: unknown, { item }: Row) => {
        if (item?.deliveredQuantity == null) return '—';
        const missing = Math.max(Number(item.quantity || 0) - Number(item.deliveredQuantity), 0);
        return (
          <span className="transport-linked-orders__stack" data-testid={`transport-linked-order-delivered-${item.id}`}>
            <span>{quantity(Number(item.deliveredQuantity), item.unit || '')}</span>
            {missing > 0 ? (
              <span className="transport-linked-orders__missing">
                {t('transport:linkedOrdersTable.missing')} {quantity(missing, item.unit || '')}
              </span>
            ) : null}
          </span>
        );
      },
    }] : []),
    ...(hasVolume || hasWeight ? [{
      key: 'load',
      title: hasVolume && hasWeight
        ? t('transport:linkedOrdersTable.volumeWeight')
        : t(hasVolume ? 'transport:volume' : 'transport:weight'),
      align: 'right' as const,
      width: '13%',
      onCell: merged,
      render: (_: unknown, { routeOrder }: Row) => {
        const volume = Number(routeOrder.volume || 0) > 0 ? `${formatLocalizedNumber(Number(routeOrder.volume), 3, i18n.language)} m³` : null;
        const weight = Number(routeOrder.weight || 0) > 0 ? `${formatLocalizedNumber(Number(routeOrder.weight), 2, i18n.language)} kg` : null;
        if (!volume && !weight) return '—';
        return (
          <span className="transport-linked-orders__stack">
            {hasVolume ? <span>{volume ?? '—'}</span> : null}
            {hasWeight ? <span className={hasVolume ? 'transport-linked-orders__secondary' : undefined}>{weight ?? '—'}</span> : null}
          </span>
        );
      },
    }] : []),
    ...(hasFreight ? [{
      key: 'freight',
      title: t('transport:linkedOrdersTable.freight'),
      align: 'right' as const,
      width: '16%',
      onCell: merged,
      render: (_: unknown, { routeOrder }: Row) => (Number(routeOrder.allocatedTransportCost || 0) > 0 ? (
        <span className="transport-linked-orders__stack">
          <TransportAllocatedCost
            allocatedTransportCost={routeOrder.allocatedTransportCost}
            originalCurrency={routeOrder.originalCurrency}
            originalAmount={routeOrder.originalAmount}
            exchangeRateUsed={routeOrder.exchangeRateUsed}
            exchangeRateSource={routeOrder.exchangeRateSource}
            exchangeRateTableNumber={routeOrder.exchangeRateTableNumber}
            exchangeRateRequestedDate={routeOrder.exchangeRateRequestedDate}
            exchangeRateEffectiveDate={routeOrder.exchangeRateEffectiveDate}
            exchangeRateIsFallback={routeOrder.exchangeRateIsFallback}
          />
          {routeOrder.allocationPercent != null ? (
            <span className="transport-linked-orders__secondary" data-testid={`transport-linked-order-share-${routeOrder.orderId}`}>
              {t('transport:allocation.percent')} {formatLocalizedNumber(routeOrder.allocationPercent, 2, i18n.language)}%
            </span>
          ) : null}
        </span>
      ) : '—'),
    }] : []),
  ];

  return (
    <Table<Row>
      className="transport-linked-orders"
      data-testid="transport-linked-order-list"
      rowKey="key"
      size="small"
      bordered
      pagination={false}
      columns={columns}
      dataSource={rows}
      tableLayout="fixed"
    />
  );
};
